/**
 * Sprint 2 — Variant helpers (pure functions, no side effects).
 * Mirror server semantics from /app/backend/models/product_variant.py.
 */

export const MAX_OPTION_GROUPS = 3;
export const MAX_OPTION_VALUES_PER_GROUP = 20;
export const MAX_ACTIVE_COMBINATIONS = 50;
export const MAX_GROUP_NAME_LEN = 40;
export const MAX_OPTION_VALUE_LEN = 40;
export const MAX_SKU_LEN = 64;

export function combinationKey(optionValues) {
  return (optionValues || [])
    .map((ov) => {
      const g = String(ov?.group ?? "").trim().toLowerCase();
      const v = String(ov?.value ?? "").trim().toLowerCase();
      return `${g}=${v}`;
    })
    .sort()
    .join("|");
}

export function cartesian(groups) {
  const cleaned = (groups || [])
    .map((g) => ({
      name: String(g?.name ?? "").trim(),
      values: (g?.values || []).map((v) => String(v ?? "").trim()).filter(Boolean),
    }))
    .filter((g) => g.name && g.values.length);
  if (!cleaned.length) return [];
  let combos = [[]];
  for (const g of cleaned) {
    const next = [];
    for (const c of combos) {
      for (const v of g.values) {
        next.push([...c, { group: g.name, value: v }]);
      }
    }
    combos = next;
  }
  return combos;
}

export function estimateCombinationCount(groups) {
  const cleaned = (groups || [])
    .map((g) => (g?.values || []).filter((v) => String(v ?? "").trim()).length)
    .filter((n) => n > 0);
  if (!cleaned.length) return 0;
  return cleaned.reduce((a, b) => a * b, 1);
}

export function validateOptionGroups(groups) {
  const errors = [];
  if (!groups || !groups.length) {
    errors.push("Tambahkan minimal 1 grup opsi.");
    return errors;
  }
  if (groups.length > MAX_OPTION_GROUPS) {
    errors.push(`Maksimal ${MAX_OPTION_GROUPS} grup opsi per produk.`);
  }
  const seenNames = new Set();
  groups.forEach((g, gi) => {
    const name = String(g?.name ?? "").trim();
    if (!name) {
      errors.push(`Grup #${gi + 1}: nama tidak boleh kosong.`);
      return;
    }
    if (name.length > MAX_GROUP_NAME_LEN) {
      errors.push(`Grup "${name}": nama melebihi ${MAX_GROUP_NAME_LEN} karakter.`);
    }
    const key = name.toLowerCase();
    if (seenNames.has(key)) {
      errors.push(`Grup "${name}": nama duplikat (case-insensitive).`);
    }
    seenNames.add(key);
    const values = (g?.values || []).map((v) => String(v ?? "").trim()).filter(Boolean);
    if (!values.length) {
      errors.push(`Grup "${name}": tambahkan minimal 1 nilai.`);
    }
    if (values.length > MAX_OPTION_VALUES_PER_GROUP) {
      errors.push(`Grup "${name}": maksimal ${MAX_OPTION_VALUES_PER_GROUP} nilai per grup.`);
    }
    const seenVals = new Set();
    values.forEach((v) => {
      if (v.length > MAX_OPTION_VALUE_LEN) {
        errors.push(`Grup "${name}": nilai "${v}" melebihi ${MAX_OPTION_VALUE_LEN} karakter.`);
      }
      const vk = v.toLowerCase();
      if (seenVals.has(vk)) {
        errors.push(`Grup "${name}": nilai duplikat "${v}".`);
      }
      seenVals.add(vk);
    });
  });
  return errors;
}

/**
 * Reconcile new cartesian output against previous variant rows.
 * - Preserve id/sku/prices/stock/weight/image_index/is_active for rows that still exist (same combinationKey)
 * - New rows are seeded with defaults (or from `defaults` param)
 * - Returns { rows, removed } where `removed` = previous rows dropped by option-group changes
 */
export function reconcileVariants(newCombos, existingRows, defaults = {}) {
  const byKey = new Map();
  for (const row of existingRows || []) {
    byKey.set(combinationKey(row.option_values || []), row);
  }
  const kept = new Set();
  const rows = newCombos.map((combo) => {
    const key = combinationKey(combo);
    const prev = byKey.get(key);
    if (prev) {
      kept.add(key);
      return { ...prev, option_values: combo, _key: key, _matched: true };
    }
    return {
      id: null,
      option_values: combo,
      sku: "",
      price: Number(defaults.price ?? 0),
      sale_price: null,
      cost_price: Number(defaults.cost_price ?? 0),
      stock: Number(defaults.stock ?? 0),
      weight_grams: Number(defaults.weight_grams ?? 0),
      image_index: null,
      is_active: true,
      _key: key,
      _matched: false,
    };
  });
  const removed = (existingRows || []).filter(
    (r) => r.id && !kept.has(combinationKey(r.option_values || [])),
  );
  return { rows, removed };
}

export function comboLabel(optionValues) {
  return (optionValues || []).map((ov) => `${ov.group}: ${ov.value}`).join(" · ");
}

export function validateVariantRow(row, opts = {}) {
  const errors = {};
  const price = Number(row.price);
  const sale = row.sale_price === null || row.sale_price === "" || row.sale_price === undefined
    ? null
    : Number(row.sale_price);
  const stock = Number(row.stock);
  const weight = Number(row.weight_grams);
  if (!Number.isFinite(price) || price < 0) errors.price = "Harga harus ≥ 0";
  if (sale !== null && (!Number.isFinite(sale) || sale < 0)) errors.sale_price = "Harga diskon harus ≥ 0";
  if (sale !== null && Number.isFinite(price) && sale > price) errors.sale_price = "≤ harga";
  if (!Number.isFinite(stock) || stock < 0 || !Number.isInteger(stock)) errors.stock = "Stok harus bilangan bulat ≥ 0";
  if (!Number.isFinite(weight) || weight < 0 || !Number.isInteger(weight)) errors.weight_grams = "Berat harus bilangan bulat ≥ 0";
  if (row.sku && String(row.sku).length > MAX_SKU_LEN) errors.sku = `Maks ${MAX_SKU_LEN} karakter`;
  if (row.image_index !== null && row.image_index !== undefined && row.image_index !== "") {
    const idx = Number(row.image_index);
    if (!Number.isInteger(idx) || idx < 0) errors.image_index = "Index gambar tidak valid";
    else if (Array.isArray(opts.images) && idx >= opts.images.length) errors.image_index = "Di luar galeri";
  }
  return errors;
}

export function toVariantPayload(row) {
  const sale = row.sale_price === null || row.sale_price === "" || row.sale_price === undefined
    ? null
    : Number(row.sale_price);
  const idx = row.image_index === null || row.image_index === "" || row.image_index === undefined
    ? null
    : Number(row.image_index);
  return {
    option_values: (row.option_values || []).map((ov) => ({ group: ov.group, value: ov.value })),
    sku: row.sku ? String(row.sku).trim() : null,
    price: Number(row.price) || 0,
    sale_price: sale,
    cost_price: row.cost_price === null || row.cost_price === "" || row.cost_price === undefined ? null : Number(row.cost_price),
    stock: Math.max(0, parseInt(row.stock, 10) || 0),
    weight_grams: Math.max(0, parseInt(row.weight_grams, 10) || 0),
    image_index: idx,
    is_active: !!row.is_active,
  };
}
